import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import {
    AUTO_CLOSE_MESSAGE_TOGGLE,
    LOADING_ENDED,
    LOADING_ENDED_ERROR,
    LOADING_STARTED,
    RESET_GLOBAL_FILTER,
    SET_CASH_REPORT_DEFAULT_FILTER,
    SET_CUSTOMERS_FEEDBACK_REPORT_DEFAULT_FILTER,
    SET_CUSTOMERS_REPORT_DEFAULT_FILTER,
    SET_EMPLOYEES_DEFAULT_FILTER,
    SET_FILTER_VALUE,
    SET_FILTERS,
    SET_GLOBAL_DEFAULT_FILTER,
    SET_ORDERS_REPORT_DEFAULT_FILTER,
    SET_PAGINATION,
    SET_SALES_BY_ITEMS_REPORT_DEFAULT_FILTER,
    SET_SPECIAL_PRICING_REPORT_DEFAULT_FILTER,
    SET_SUMMARY_REPORT_DEFAULT_FILTER,
    SET_TIMESHEET_REPORT_DEFAULT_FILTER,
    SET_TOTAL_COUNT,
    SET_TRANSACTIONS_REPORT_DEFAULT_FILTER,
    SET_VOIDS_REPORT_DEFAULT_FILTER,
} from "./actions"
import {
    createCashReportDefaultFilter,
    createCustomersFeedbackReportDefaultFilter,
    createCustomersReportDefaultFilter,
    createEmployeesDefaultFilter,
    createGlobalDefaultFilter,
    createOrdersReportDefaultFilter,
    createSalesByItemsReportDefaultFilter,
    createSpecialPricingsLogReportDefaultFilter,
    createSummaryReportDefaultFilter,
    createTimesheetReportDefaultFilter,
    createTransactionsReportDefaultFilter,
    createVoidsLogReportDefaultFilter,
} from "./utils"
import type { FiltersIdentifier, FiltersObject } from "./types"
import { LOG_OUT_FAILURE, LOG_OUT_SUCCESS } from "../layouts/header/actions"
import type { AutoCloseMessageType } from "../common/components/AutoCloseMessage/AutoCloseMessageComponent"
import { REFETCH_DATA } from "../auth/actions"

export type LoadingStateObject = {
    count: number
    error: boolean
}

export type FiltersState = {
    loadings: Record<string, LoadingStateObject>
    filters: Record<FiltersIdentifier, FiltersObject>
    autoCloseMessage: Maybe<AutoCloseMessage>
    reloadButtonClickedCount: number
}

export type AutoCloseMessage = {
    text: string
    type: AutoCloseMessageType
}

export const GLOBAL_FILTER_IDENTIFIER = "global"

export const createDefaultFiltersState = (): FiltersState => ({
    loadings: {
        orders: {
            count: 0,
            error: false,
        },
        customers: {
            count: 2,
            error: false,
        },
        customersFeedback: {
            count: 2,
            error: false,
        },
        cash: {
            count: 0,
            error: false,
        },
        timesheet: {
            count: 0,
            error: false,
        },
        transactionsReport: {
            count: 0,
            error: false,
        },
        netCollectedBreakdownDataLoading: {
            count: 0,
            error: false,
        },
        netCollectedByEmployeeDataLoading: {
            count: 0,
            error: false,
        },
        netCollectedByEmployeeDetailDataLoading: {
            count: 0,
            error: false,
        },
        netRevenueByCategoryDataLoading: {
            count: 0,
            error: false,
        },
        netRevenueByAssignmentDataLoading: {
            count: 0,
            error: false,
        },
        transactionsByPaymentTypeDataLoading: {
            count: 0,
            error: false,
        },
        taxation: {
            count: 2,
            error: false,
        },
        pricing: {
            count: 2,
            error: false,
        },
        locationsAndterminals: {
            count: 0,
            error: false,
        },
        itemsAndCategories: {
            count: 2,
            error: false,
        },
        assignments: {
            count: 0,
            error: false,
        },
        employees: {
            count: 2,
            error: false,
        },
        settings: {
            count: 2,
            error: false,
        },
    },
    reloadButtonClickedCount: 0,
    filters: {
        [GLOBAL_FILTER_IDENTIFIER]: {
            location_name: [],
            selectedMibTerminalId: null,
            selectedMibSingleTerminalId: null,
            timestamp: null,
            date: null,
        },
    },
    autoCloseMessage: null,
})
export const filtersReducer = (state: Maybe<FiltersState> = undefined, action: Record<string, any>) => {
    if (!state) {
        return createDefaultFiltersState()
    }

    switch (action.type) {
        case LOADING_STARTED:
            return {
                ...state,
                loadings: {
                    ...state.loadings,
                    [action.payload.identifier]: {
                        count: 1,
                        error: false,
                    },
                },
            }

        case LOADING_ENDED:
            return {
                ...state,
                loadings: {
                    ...state.loadings,
                    [action.payload.identifier]: { ...state.loadings[action.payload.identifier], count: 0 },
                },
            }

        case LOADING_ENDED_ERROR:
            return {
                ...state,
                loadings: {
                    ...state.loadings,
                    [action.payload.identifier]: {
                        ...state.loadings[action.payload.identifier],
                        count: 0,
                        error: true,
                    },
                },
            }

        case REFETCH_DATA:
            return {
                ...state,
                reloadButtonClickedCount: state.reloadButtonClickedCount + 1,
            }

        case SET_FILTER_VALUE:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [action.payload.identifier]: {
                        ...state.filters[action.payload.identifier],
                        [action.payload.type]: {
                            ...(state.filters[action.payload.identifier] &&
                                state.filters[action.payload.identifier][action.payload.type]),
                            ...action.payload.filterValue,
                        },
                    },
                },
            }

        case SET_FILTERS:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [action.payload.identifier]: {
                        ...state.filters[action.payload.identifier],
                        ...action.payload.filters,
                    },
                },
            }

        case SET_PAGINATION:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [action.payload.identifier]: {
                        ...state.filters[action.payload.identifier],
                        pagination: {
                            ...(state.filters[action.payload.identifier] &&
                                state.filters[action.payload.identifier].pagination),
                            ...action.payload.paginationValue,
                        },
                    },
                },
            }

        case SET_TOTAL_COUNT:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [action.payload.identifier]: {
                        ...state.filters[action.payload.identifier],
                        pagination: {
                            ...(state.filters[action.payload.identifier] &&
                                state.filters[action.payload.identifier].pagination),
                            totalCount: action.payload.totalCount,
                        },
                    },
                },
            }

        case SET_GLOBAL_DEFAULT_FILTER:
            return createGlobalDefaultFilter(
                state,
                action.payload.locationsIds,
                action.payload.from,
                action.payload.to,
                action.payload.fromDate,
                action.payload.toDate
            )

        case RESET_GLOBAL_FILTER:
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [GLOBAL_FILTER_IDENTIFIER]: {
                        location_name: [],
                        selectedMibTerminalId: null,
                        selectedMibSingleTerminalId: null,
                        timestamp: null,
                        date: null,
                    },
                },
            }

        case SET_SPECIAL_PRICING_REPORT_DEFAULT_FILTER: {
            return createSpecialPricingsLogReportDefaultFilter(state)
        }

        case SET_VOIDS_REPORT_DEFAULT_FILTER: {
            return createVoidsLogReportDefaultFilter(state)
        }

        case SET_SALES_BY_ITEMS_REPORT_DEFAULT_FILTER:
            return createSalesByItemsReportDefaultFilter(state)

        case SET_CASH_REPORT_DEFAULT_FILTER:
            return createCashReportDefaultFilter(state)

        case SET_CUSTOMERS_REPORT_DEFAULT_FILTER:
            return createCustomersReportDefaultFilter(state)

        case SET_CUSTOMERS_FEEDBACK_REPORT_DEFAULT_FILTER:
            return createCustomersFeedbackReportDefaultFilter(state)

        case SET_TRANSACTIONS_REPORT_DEFAULT_FILTER:
            return createTransactionsReportDefaultFilter(state)

        case SET_ORDERS_REPORT_DEFAULT_FILTER:
            return createOrdersReportDefaultFilter(state)

        case SET_SUMMARY_REPORT_DEFAULT_FILTER:
            return createSummaryReportDefaultFilter(state)

        case SET_TIMESHEET_REPORT_DEFAULT_FILTER:
            return createTimesheetReportDefaultFilter(state)

        case SET_EMPLOYEES_DEFAULT_FILTER:
            return createEmployeesDefaultFilter(state)

        case LOG_OUT_SUCCESS:
        case LOG_OUT_FAILURE:
            return createDefaultFiltersState()

        case AUTO_CLOSE_MESSAGE_TOGGLE:
            return { ...state, autoCloseMessage: action.payload.autoCloseMessage }

        default:
            return state
    }
}
